import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, X, Plus, AlertCircle } from 'lucide-react';
import api from '../api';
import VociListino, { FREE_MAX_VOCI } from '../components/VociListino';
import { tipoLabel } from '../utils/tipi';
import usePiano from '../hooks/usePiano';
import ConfirmModal from '../components/ConfirmModal';
import PlanModal from '../components/PlanModal';
import './CreaServizio.css';
import './ModificaServizio.css';

// Converte una voce dal formato DB (numeri/null) a quello del form (stringhe).
const voceFromDb = (v) => ({
  tipo: v.tipo || 'principale',
  titolo: v.titolo || '',
  prezzo: v.prezzo ?? '',
  prezzo_modalita: v.prezzo_modalita || 'fisso',
  durata: v.durata || '',
  max_bambini: v.max_bambini ?? '',
  prezzo_bambino_extra: v.prezzo_bambino_extra ?? '',
  eta_min: v.eta_min ?? '',
  eta_max: v.eta_max ?? '',
  cosa_include: Array.isArray(v.cosa_include) ? v.cosa_include : [],
  opzioni: Array.isArray(v.opzioni) ? v.opzioni.map((o) => ({ etichetta: o.etichetta || '', prezzo: o.prezzo ?? '' })) : [],
  note: v.note || '',
});

// Tetto foto Pro (i free hanno FREE_MAX_FOTO, sotto).
const MAX_FOTO_PRO = 5;
const FREE_MAX_FOTO = 1;

function ModificaServizio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPro } = usePiano();
  const MAX_FOTO = isPro ? MAX_FOTO_PRO : FREE_MAX_FOTO;
  const [categorieSuggerite, setCategorieSuggerite] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'location',
    descrizione: '',
    prezzo_min: '',
    prezzo_max: '',
    eta_minima: '',
    eta_massima: '',
    indoor_outdoor: 'indoor',
    categoria: '',
  });
  const [elenco, setElenco] = useState([]);
  const [nuovaVoce, setNuovaVoce] = useState('');
  const [voci, setVoci] = useState([]);
  const [existingFoto, setExistingFoto] = useState([]);
  const [newFoto, setNewFoto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    const loadServizio = async () => {
      try {
        const res = await api.get(`/servizi/${id}`);
        const s = res.data;
        setFormData({
          nome: s.nome || '',
          tipo: s.tipo || 'location',
          descrizione: s.descrizione || '',
          prezzo_min: s.prezzo_min ?? '',
          prezzo_max: s.prezzo_max ?? '',
          eta_minima: s.eta_minima ?? '',
          eta_massima: s.eta_massima ?? '',
          indoor_outdoor: s.indoor_outdoor || 'indoor',
          categoria: s.categoria || '',
        });
        setElenco(Array.isArray(s.elenco_attivita) ? s.elenco_attivita : []);
        setVoci(Array.isArray(s.voci) ? s.voci.map(voceFromDb) : []);
        setExistingFoto(s.foto || []);

        const catRes = await api.get(`/categorie?tipo=${s.tipo}`);
        setCategorieSuggerite(catRes.data.categorie || []);
      } catch (err) {
        setError('Errore nel caricamento della scheda');
      } finally {
        setLoading(false);
      }
    };
    loadServizio();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const aggiungiVoce = () => {
    const v = nuovaVoce.trim();
    if (v && !elenco.includes(v)) setElenco((prev) => [...prev, v]);
    setNuovaVoce('');
  };
  const rimuoviVoce = (idx) => setElenco((prev) => prev.filter((_, i) => i !== idx));

  const handleNewFotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setNewFoto((prev) => {
      const spazio = MAX_FOTO - existingFoto.length - prev.length;
      if (spazio <= 0) {
        setError(`Puoi avere al massimo ${MAX_FOTO} foto per scheda.`);
        return prev;
      }
      if (files.length > spazio) {
        setError(`Puoi avere al massimo ${MAX_FOTO} foto: ne sono state aggiunte solo ${spazio}.`);
      }
      return [...prev, ...files.slice(0, spazio).map((file) => ({ file, preview: URL.createObjectURL(file) }))];
    });
    e.target.value = '';
  };
  const removeNewFoto = (idx) => {
    setNewFoto((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const eseguiRimozioneFoto = async (fotoId) => {
    setModal(null);
    try {
      await api.delete(`/upload/foto/${fotoId}`);
      setExistingFoto((prev) => prev.filter((f) => f.id !== fotoId));
    } catch (err) {
      setModal({
        title: 'Errore',
        message: 'Errore nell\'eliminazione della foto. Riprova.',
        confirmLabel: 'Ho capito', cancelLabel: null,
        onConfirm: () => setModal(null),
      });
    }
  };

  const removeExistingFoto = (fotoId) => {
    setModal({
      title: 'Eliminare la foto?',
      message: 'La foto verrà rimossa dalla scheda. L\'azione non è reversibile.',
      variant: 'danger', confirmLabel: 'Elimina', cancelLabel: 'Annulla',
      onConfirm: () => eseguiRimozioneFoto(fotoId),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (!formData.nome.trim()) throw new Error('Il nome è obbligatorio');
      if (!formData.descrizione.trim()) throw new Error('La descrizione è obbligatoria');

      await api.put(`/servizi/${id}`, {
        nome: formData.nome,
        descrizione: formData.descrizione,
        eta_minima: formData.eta_minima === '' ? null : formData.eta_minima,
        eta_massima: formData.eta_massima === '' ? null : formData.eta_massima,
        indoor_outdoor: formData.indoor_outdoor,
        categoria: formData.categoria || null,
        elenco_attivita: elenco,
        voci,
      });

      if (newFoto.length > 0) {
        const fd = new FormData();
        newFoto.forEach((f) => fd.append('files', f.file));
        await api.post(`/upload/servizio/${id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate('/dashboard', { state: { success: 'Scheda aggiornata con successo!' } });
    } catch (err) {
      setError(err.response?.data?.errore || err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="modifica-page loading"><div className="spinner" />Caricamento...</div>;
  }

  if (error && !formData.nome) {
    return (
      <div className="modifica-page error">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Errore</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Torna alla dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="crea-servizio-page">
      <div className="form-container">
        <h1>Modifica scheda</h1>

        <form onSubmit={handleSubmit} className="servizio-form">
          {error && <div className="error-message">{error}</div>}

          <fieldset>
            <legend>Informazioni di Base</legend>
            <div className="form-group">
              <label htmlFor="nome">Nome della scheda *</label>
              <input type="text" id="nome" name="nome" value={formData.nome} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="tipo-readonly">Categoria</label>
              <input type="text" id="tipo-readonly" value={tipoLabel(formData.tipo)} disabled />
              <small className="form-hint">La categoria non è modificabile dopo la creazione.</small>
            </div>
            <div className="form-group">
              <label htmlFor="categoria">Sottocategoria</label>
              <input type="text" id="categoria" name="categoria" list="categorie-list"
                value={formData.categoria} onChange={handleChange} />
              <datalist id="categorie-list">
                {categorieSuggerite.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label htmlFor="descrizione">Descrizione * (no link/email)</label>
              <textarea id="descrizione" name="descrizione" rows="6"
                value={formData.descrizione} onChange={handleChange} required />
            </div>
          </fieldset>

          <fieldset>
            <legend>Listino — pacchetti e prezzi</legend>
            <VociListino
              value={voci} onChange={setVoci}
              maxVoci={isPro ? Infinity : FREE_MAX_VOCI}
              onUpgrade={() => setPlanOpen(true)}
            />
          </fieldset>

          <fieldset>
            <legend>Cosa offri</legend>
            <div className="form-group">
              <div className="elenco-input-row">
                <input type="text" value={nuovaVoce}
                  onChange={(e) => setNuovaVoce(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aggiungiVoce(); } }}
                  placeholder="Es. Trucca bimbi" />
                <button type="button" className="btn btn-secondary" onClick={aggiungiVoce}>
                  <Plus size={18} /> Aggiungi
                </button>
              </div>
              {elenco.length > 0 && (
                <div className="chips">
                  {elenco.map((v, idx) => (
                    <span key={idx} className="chip">
                      {v}
                      <button type="button" onClick={() => rimuoviVoce(idx)}><X size={14} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend>Dettagli</legend>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eta_minima">Età minima</label>
                <input type="number" id="eta_minima" name="eta_minima" min="0" max="18"
                  value={formData.eta_minima} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="eta_massima">Età massima</label>
                <input type="number" id="eta_massima" name="eta_massima" min="0" max="18"
                  value={formData.eta_massima} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="indoor_outdoor">Ubicazione</label>
              <select id="indoor_outdoor" name="indoor_outdoor"
                value={formData.indoor_outdoor} onChange={handleChange}>
                <option value="indoor">Al chiuso</option>
                <option value="outdoor">All'aperto</option>
                <option value="entrambi">Interno + esterno</option>
              </select>
            </div>
          </fieldset>

          <fieldset>
            <legend>Foto (max {MAX_FOTO})</legend>
            {!isPro && (
              <p className="form-hint">
                Il piano gratuito permette {FREE_MAX_FOTO} foto.{' '}
                <button type="button" className="link-button" onClick={() => setPlanOpen(true)}>
                  Passa a Pro
                </button>{' '}
                per caricarne fino a {MAX_FOTO_PRO}.
              </p>
            )}
            {existingFoto.length > 0 && (
              <div className="foto-preview">
                <h3>Foto attuali</h3>
                <div className="preview-grid">
                  {existingFoto.map((f) => (
                    <div key={f.id} className="preview-item">
                      <img src={f.url} alt={f.alt_text || 'Foto'} />
                      <button type="button" className="remove-btn" onClick={() => removeExistingFoto(f.id)}>
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {existingFoto.length + newFoto.length < MAX_FOTO ? (
              <div className="upload-area">
                <input type="file" id="foto" multiple accept="image/*" onChange={handleNewFotoChange} />
                <label htmlFor="foto" className="upload-label">
                  <Upload size={32} />
                  <p>Aggiungi nuove foto</p>
                  <span>JPG, PNG fino a 5 MB · max {MAX_FOTO} foto in totale</span>
                </label>
              </div>
            ) : (
              <p className="form-hint">Hai raggiunto il massimo di {MAX_FOTO} foto. Rimuovine una per aggiungerne altre.</p>
            )}
            {newFoto.length > 0 && (
              <div className="foto-preview">
                <h3>Nuove foto ({newFoto.length})</h3>
                <div className="preview-grid">
                  {newFoto.map((f, idx) => (
                    <div key={idx} className="preview-item">
                      <img src={f.preview} alt={`Preview ${idx}`} />
                      <button type="button" className="remove-btn" onClick={() => removeNewFoto(idx)}>
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </fieldset>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        message={modal?.message}
        variant={modal?.variant}
        confirmLabel={modal?.confirmLabel}
        cancelLabel={modal?.cancelLabel}
        onConfirm={modal?.onConfirm || (() => setModal(null))}
        onClose={() => setModal(null)}
      />
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} />
    </div>
  );
}

export default ModificaServizio;
